import { createSelector, Dispatch } from '@reduxjs/toolkit';
import {
    selectActivityContentLocale,
    selectCurrentReviewFilter,
    selectCurrentReviewSort,
    selectReviewResultList,
    selectReviewResultPagination,
    selectReviewResultTotalFilteredReviews,
    selectReviewState,
} from 'component/page/detail/store/detail.selectors';
import { ActivityReview } from 'gql/autogen/ActivityReview';
import { MediaType } from 'gql/autogen/MediaType';
import { ReviewDataSource } from 'gql/autogen/ReviewDataSource';
import { ReplyAuthorType } from 'gql/autogen/ReplyAuthorType';
import { DetailContentOnToggleProps } from 'component/page/detail/DetailPageContent/DetailPageContent.selectors';
import { Pagination } from 'gql/autogen/Pagination';
import { navigateToHashWithTabBarOffset } from 'component/common/DetailInViewWrapper/navigateToHashWithTabBarOffset';
import { NavigationHashEnum } from 'core/type/Details.types';
import { selectActivityIdFromBootstrap, selectCmsFromBootstrap } from 'store/slice/bootstrap';
import { CmsValue } from 'core/type/BootstrapConfig.types';
import { ReviewSortState } from 'component/page/detail/Review/ReviewSortAndFilter/ReviewSortAndFilter.types';
import { Filters } from 'gql/autogen/Filters';
import { fetchReview, resetReviewSortFilter } from 'component/page/detail/store/review';
import { trackElementEvent } from 'core/messaging/analytics/analytics';
import { ActivitiesNoResultClearFilterClickEventActivitiesFieldsV1 } from '@agoda/analytics-messages-definition-activities/events.split';
import { ActivitiesNoResultClearFilterClickEventActivitiesManifestV1 } from '@agoda/analytics-messages-definition-activities/manifest/events';
import { TranslationResult } from 'core/type/TranslationResult.types';
import { setIsScrolling } from 'component/page/detail/store/details';
import { selectTabs } from 'component/page/detail/DetailPageTabs/DetailPageTabs.selectors';
import { ScreenSize, selectScreenSize } from 'store/slice/screen';
import { SortOrder } from 'gql/autogen/SortOrder';
import { SortCode } from 'gql/autogen/SortCode';
import { ReviewSectionSpecificProps } from 'component/page/detail/DetailPageModularize/detailPageModularize.types';

export interface ReviewCardProps {
    id: number;
    authorName: string;
    date: string;
    title: string;
    score: number;
    source: ReviewSourceDisplay;
    description: string;
    media: ReviewMediaDisplay[];
    voteCount: number;
    replies: ReviewReplyDisplay[];
    isTranslationRequired: boolean;
    translatedReview?: TranslationResult;
}

export interface ReviewTranslationStateProps {
    translationState: { isLoading: boolean; translationIndex: number };
    setTranslationState: (translationState: { isLoading: boolean; translationIndex: number }) => void;
}

export interface ReviewTranslationContextProps {
    isLoading: boolean;
    translationIndex: number;
}

export enum ReviewSourceDisplay {
    KKDAY = 'KKday',
    VIATOR = 'Viator',
    TRIP_ADVISOR = 'Tripadvisor',
    UNKNOWN = '',
}

export enum ReviewReplyAuthorTypeCmsId {
    SUPPLIER = 277208,
    HOST = 277207,
    UNKNOWN = 277209,
}

const ReviewDataSourceToSourceDisplay: Record<ReviewDataSource, ReviewSourceDisplay> = {
    [ReviewDataSource.KKDAY]: ReviewSourceDisplay.KKDAY,
    [ReviewDataSource.VIATOR]: ReviewSourceDisplay.VIATOR,
    [ReviewDataSource.TRIP_ADVISOR]: ReviewSourceDisplay.TRIP_ADVISOR,
    [ReviewDataSource.UNKNOWN]: ReviewSourceDisplay.UNKNOWN,
};

const ReplyAuthorTypeCmsId: Record<ReplyAuthorType, number> = {
    [ReplyAuthorType.SUPPLIER]: ReviewReplyAuthorTypeCmsId.SUPPLIER,
    [ReplyAuthorType.HOST]: ReviewReplyAuthorTypeCmsId.HOST,
    [ReplyAuthorType.UNKNOWN]: ReviewReplyAuthorTypeCmsId.UNKNOWN,
};

export interface ReviewMediaDisplay {
    source: string;
    description: string;
    mediaType: string;
}

interface StateProps {
    isLoading: boolean;
    isSuccess: boolean;
    reviewList: ReviewCardProps[];
    contentLocale: string;
    cms: CmsValue;
    currentFilter: Filters;
    isHideReview: boolean;
    activityId: number;
    screenSize: ScreenSize;
    currentSort: ReviewSortState;
}

interface DispatchProps {
    onClearFilterCreator: (activityId: number) => () => void;
}

export interface StatePropsReviewPagination {
    isLoading: boolean;
    isSuccess: boolean;
    cms: CmsValue;
    currentSort?: ReviewSortState;
    currentFilter?: Filters;
    currentPageIndex?: number;
    maxPageNumber?: number;
    pagination?: Pagination;
    activityId: number;
    reviewSectionHref: string;
}

interface DispatchPropsReviewPagination {
    onPaginationClickCreator: (
        activityId: number,
        reviewSectionHref: string,
        currentSort: ReviewSortState | undefined,
        currentFilter: Filters | undefined,
    ) => (pageNumber: number, pageSize: number) => void;
}

export type ReviewPaginationProps = StatePropsReviewPagination & DispatchPropsReviewPagination;

export interface ReviewReplyDisplay {
    replyId: number;
    replyAuthorType: string;
    replyDate: string;
    replyText: string;
}

interface ReviewListOwnProps {
    isInOverlay?: boolean;
}

export type ReviewListProps = StateProps & DispatchProps & ReviewListOwnProps;

export type ReviewOverviewCarouselProps = StateProps & DispatchProps;

export type ReviewSectionProps = StateProps & DetailContentOnToggleProps;

export type ReviewSectionModularProps = StateProps & ReviewSectionSpecificProps;

export const mapReviewCardProps = (reviews: ActivityReview[], cms: CmsValue): ReviewCardProps[] => {
    return reviews.map((review) => {
        const reviewMedia = review.media
            ?.filter((m) => m.mediaType === MediaType.IMAGE)
            .map((m) => {
                return { source: m.source || '', description: m.description || '', mediaType: m.mediaType || '' };
            });
        const reviewReplies = review.replies
            ?.filter((r) => r && r.replyText !== '')
            ?.map((r) => {
                return {
                    replyId: r.replyId || 0,
                    replyAuthorType: cms[ReplyAuthorTypeCmsId[r.authorType || ReplyAuthorType.UNKNOWN]],
                    replyDate: r.replyDateTime || '',
                    replyText: r.replyText || '',
                };
            });

        return {
            id: review.reviewId || 0,
            authorName: review.authorName || cms[73619],
            date: review.reviewDateTime || '',
            title: review.title || '',
            score: review.reviewScore || 0,
            source: ReviewDataSourceToSourceDisplay[review.reviewSource || ReviewDataSource.UNKNOWN],
            description: review.reviewText || '',
            voteCount: review.helpfulVoteCount || 0,
            isTranslationRequired: review.isTranslationRequired || false,
            media: reviewMedia || [],
            replies: reviewReplies || [],
            translatedReview: undefined,
        };
    });
};

export const mapStateToProps = createSelector(
    selectCmsFromBootstrap,
    selectReviewState,
    selectReviewResultList,
    selectActivityContentLocale,
    selectActivityIdFromBootstrap,
    selectScreenSize,
    selectCurrentReviewSort,
    (cms, state, reviews, contentLocale, activityId, screenSize, currentSort): StateProps => {
        if (state.isLoading) {
            return {
                reviewList: [],
                isLoading: state.isLoading,
                isSuccess: state.isSuccess,
                contentLocale,
                cms,
                currentFilter: state.currentFilter,
                isHideReview: state.isHideReview,
                activityId,
                screenSize,
                currentSort,
            };
        }
        const reviewList = mapReviewCardProps(reviews || [], cms);
        return {
            reviewList,
            isLoading: state.isLoading,
            isSuccess: state.isSuccess,
            contentLocale,
            cms,
            currentFilter: state.currentFilter,
            isHideReview: state.isHideReview,
            activityId,
            screenSize,
            currentSort,
        };
    },
);

export const mapDispatchToProps = (dispatch: Dispatch): DispatchProps => ({
    onClearFilterCreator: (activityId: number) => () => {
        dispatch(resetReviewSortFilter());
        fetchReview(activityId, { sort: { code: SortCode.Default, order: SortOrder.Default } }, { filters: { valueFilters: [], rangeFilters: [] } });
        trackElementEvent<ActivitiesNoResultClearFilterClickEventActivitiesFieldsV1>(ActivitiesNoResultClearFilterClickEventActivitiesManifestV1.ids, {});
    },
});

export const mapStateToPropsReviewPagination = createSelector(
    selectReviewState,
    selectReviewResultPagination,
    selectReviewResultTotalFilteredReviews,
    selectCmsFromBootstrap,
    selectCurrentReviewSort,
    selectCurrentReviewFilter,
    selectActivityIdFromBootstrap,
    selectTabs,
    (state, pagination, totalFilteredReviews, cms, currentReviewSort, currentReviewFilter, activityId, tabsObject): StatePropsReviewPagination => {
        const currentFilter = currentReviewFilter;
        const currentSort = currentReviewSort;
        const { tabs } = tabsObject;
        const reviewSectionTab = tabs.find((tab) => tab.id === NavigationHashEnum.ReviewSection) || { href: '' };
        if (state.isLoading) {
            return {
                isLoading: state.isLoading,
                isSuccess: state.isSuccess,
                cms,
                currentFilter,
                currentSort,
                maxPageNumber: undefined,
                currentPageIndex: undefined,
                pagination: undefined,
                activityId,
                reviewSectionHref: reviewSectionTab.href,
            };
        }
        const currentPageNumber = pagination && pagination.number;
        const currentPageIndex = currentPageNumber && currentPageNumber > 0 ? currentPageNumber - 1 : undefined;
        const maxPageNumber = pagination && pagination.size && totalFilteredReviews && Math.ceil(totalFilteredReviews / pagination.size);
        return {
            isLoading: state.isLoading,
            isSuccess: state.isSuccess,
            cms,
            currentFilter,
            currentSort,
            maxPageNumber,
            currentPageIndex,
            pagination,
            activityId,
            reviewSectionHref: reviewSectionTab.href,
        };
    },
);

export const mapDispatchToPaginationProps = (dispatch: Dispatch): DispatchPropsReviewPagination => ({
    onPaginationClickCreator: (activityId: number, reviewSectionHref: string, currentSort?: ReviewSortState, currentFilter?: Filters) => (pageNumber: number, pageSize: number) => {
        fetchReview(
            activityId,
            currentSort && { sort: { code: currentSort.code, order: currentSort.order } },
            currentFilter && { filters: { valueFilters: currentFilter.valueFilters, rangeFilters: currentFilter.rangeFilters } },
            pageNumber,
            pageSize,
        );
        const FETCH_REVIEW_WAIT_TIME = 100;
        setTimeout(() => {
            navigateToHashWithTabBarOffset(dispatch, `#${reviewSectionHref}`, setIsScrolling, true);
        }, FETCH_REVIEW_WAIT_TIME);
    },
});
